import { Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { Bars3Icon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../contexts/AuthContext'
import { useBranch } from '../contexts/BranchContext'
import { cn } from '../utils/cn'
import BranchSelector from './BranchSelector'
import NotificationBell from './NotificationBell'

export default function Header({ sidebarOpen, setSidebarOpen, currentBranch, userRole }) {
  const { currentUser, userProfile, signOut } = useAuth()
  const { role } = useAuth()

  const userNavigation = [
    { name: 'Your Profile', href: '/profile' },
    { name: 'Sign out', action: signOut },
  ]

  return (
    <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-border bg-card px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
      <button
        type="button"
        className="-m-2.5 p-2.5 text-foreground lg:hidden"
        onClick={() => setSidebarOpen(true)}
      >
        <span className="sr-only">Open sidebar</span>
        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
      </button>

      {/* Separator */}
      <div className="h-6 w-px bg-border lg:hidden" aria-hidden="true" />

      <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
        <div className="flex flex-1 items-center">
          {/* Branch Selector for owners */}
          {role === 'owner' && (
            <BranchSelector />
          )}
          
          {/* Current branch display for non-owners */}
          {role !== 'owner' && currentBranch && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{currentBranch.name}</span>
              <span className="hidden text-xs sm:inline">({role})</span>
            </div>
          )}
          {role !== 'owner' && !currentBranch && (
            <div className="text-sm text-amber-700">
              No branch assigned — contact an administrator
            </div>
          )}
        </div>

        <div className="flex items-center gap-x-4 lg:gap-x-6">
          {/* Notification Bell */}
          <NotificationBell />
          
          {/* Profile dropdown */}
          <Menu as="div" className="relative">
            <Menu.Button className="-m-1.5 flex items-center p-1.5">
              <span className="sr-only">Open user menu</span>
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                <span className="text-sm font-medium text-primary-foreground">
                  {currentUser?.displayName?.charAt(0) || userProfile?.name?.charAt(0) || 'U'}
                </span>
              </div>
              <span className="hidden lg:flex lg:items-center">
                <span className="ml-4 text-sm font-semibold leading-6 text-foreground" aria-hidden="true">
                  {currentUser?.displayName || userProfile?.name || 'User'}
                </span>
                <ChevronDownIcon className="ml-2 h-5 w-5 text-muted-foreground" aria-hidden="true" />
              </span>
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2.5 w-44 origin-top-right rounded-md bg-card py-2 shadow-lg ring-1 ring-border focus:outline-none">
                {userNavigation.map((item) => (
                  <Menu.Item key={item.name}>
                    {({ active }) => (
                      <button
                        onClick={item.action || (() => {})}
                        className={cn(
                          active ? 'bg-muted/60' : '',
                          'block w-full px-3 py-2 text-left text-sm leading-6 text-foreground'
                        )}
                      >
                        {item.name}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  )
} 